import Portal from '@reach/portal';
import { useRect } from '@reach/rect';
import React__default, { createContext, useContext, useMemo, useRef, createRef } from 'react';
import { animated, useTransition, config } from 'react-spring';
import AutoSizer from 'react-virtualized-auto-sizer';
import styled from 'styled-components';
import { useInfiniteScroll } from '../../lib/infiniteScroll.js';
import { useIntersectionObserver } from '../../lib/intersection.js';
import { useTranslate } from '../../lib/translate.js';
import { Badge } from '../badge/Badge.js';
import { HappyWebSystemError, useQueryChildComponents } from '../base/SystemComponent.js';
import { Button } from '../button/Button.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Fallback } from '../fallback/Fallback.js';
import { CheckboxInput } from '../inputs/CheckboxInput.js';
import { Box } from '../layout/Box.js';
import { Inline } from '../layout/Inline.js';
import { useListSelection } from '../list/List.js';
import { LoadingSpinnerOverlay, LoadingSpinner } from '../loading/LoadingSpinner.js';
import { getThemeVariantColor, setPortalLayer } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { TableRow, useTableRow, TableData, TableOverflowMenu, TableDataSpacer } from './TableData.js';
export { TableData, TableIcon, TableOverflowMenu, TableRow } from './TableData.js';
import { TableHeaderRow } from './TableHeader.js';
export { TableHeader, TableHeaderRow, TableHeaderSpacer, TableOverflowMenuHeader } from './TableHeader.js';
import { SCROLL_HIGHLIGHT_WIDTH_REM } from './TableScrollHighlight.js';

const TableContext = createContext(undefined);
const useTable = ()=>{
    const context = useContext(TableContext);
    if (!context) {
        throw new HappyWebSystemError("useTable", "Missing TableContext - a TableContextProvider must be set.");
    }
    return context;
};
const TableProvider = ({ selected , allIDs , toggle , toggleAll , children  })=>{
    const value = useMemo(()=>({
            selected,
            allIDs,
            toggle,
            toggleAll
        }), [
        selected,
        allIDs,
        toggle,
        toggleAll
    ]);
    return React__default.createElement(TableContext.Provider, {
        value: value
    }, children);
};
const TableRefsContext = createContext(undefined);
const useTableRefs = ()=>{
    const context = useContext(TableRefsContext);
    if (!context) {
        throw new HappyWebSystemError("useTableRefs", "Missing TableRefsContext - a TableRefsContextProvider must be set.");
    }
    return context;
};
const TableRefsProvider = ({ containerRef , scrollContainerRef , scrollVerticalElementRef , children  })=>{
    const headerRefs = useRef([]);
    const value = useMemo(()=>({
            containerRef,
            scrollContainerRef,
            scrollVerticalElementRef,
            headerRefs: headerRefs.current
        }), [
        containerRef,
        scrollContainerRef,
        scrollVerticalElementRef
    ]);
    return React__default.createElement(TableRefsContext.Provider, {
        value: value
    }, children);
};
const StyledTable = styled.table`
  position: relative;
  border-collapse: collapse;
  border: 0;
  display: grid;
  grid-template-columns: ${(props)=>{
    let gridTemplate = "";
    for (const g of props.$gridMinMax){
        gridTemplate = `${gridTemplate} minmax(${g.min}rem, ${g.max}rem)`;
    }
    return gridTemplate;
}};
  grid-template-rows: ${(props)=>`repeat(${props.$rows + 1}, 4.5rem)`};

  /* Important that the table has a height explicitly set, otherwise sticky headers do not work */
  height: ${(props)=>(props.$rows + 1) * 4.5}rem;

  /* Elements within the table need to be ignored, so that grid css can apply directly to the td cells */
  & thead,
  & tbody,
  & tr {
    display: contents;
  }

  /* Handle row hover defaults from the table's styling, as we need the last column element number */
  & tr td:nth-child(1) [data-hpy-web-table-data-inner] {
    border-left: 1px solid transparent;
    border-radius: 0.5rem 0 0 0.5rem;
  }

  & tr td:nth-child(${(props)=>props.$gridMinMax.length}) [data-hpy-web-table-data-inner] {
    border-right: 1px solid transparent;
    border-radius: 0 0.5rem 0.5rem 0;
  }

  & tr td [data-hpy-web-table-data-inner] {
    border-bottom: 1px solid transparent;
    border-top: 1px solid transparent;
  }

  & tr:not([data-hpy-web-table-highlight='true']):hover td [data-hpy-web-table-data-inner] {
    border-color: ${COLOR_PALETTE.slateGrey10};
  }

  & tr[data-hpy-web-table-highlight='true'] td [data-hpy-web-table-data-inner] {
    border-color: ${getThemeVariantColor("context")};
  }

  /* Show columns 'scrolling' underneath table */
  &[data-hpy-web-table-grid-leftscroll='true'] th:nth-child(1) [data-hpy-web-table-scrollhighlight-left],
  &[data-hpy-web-table-grid-leftscroll='true'] td:nth-child(1) [data-hpy-web-table-scrollhighlight-left] {
    display: block;
  }

  &[data-hpy-web-table-grid-rightscroll='true']
    th:nth-child(${(props)=>props.$gridMinMax.length})
    [data-hpy-web-table-scrollhighlight-right],
  &[data-hpy-web-table-grid-rightscroll='true']
    td:nth-child(${(props)=>props.$gridMinMax.length})
    [data-hpy-web-table-scrollhighlight-right] {
    display: block;
  }

  /* Don't render the space for the overflow menu if the table doesn't have overflow items to show */
  &[data-hpy-web-table-grid-hide-overflow-column='true'] [data-hpy-web-table-overflow-menu-header],
  &[data-hpy-web-table-grid-hide-overflow-column='true'] [data-hpy-web-table-overflow-menu],
  &[data-hpy-web-table-grid-hide-overflow-column='true']
    [data-hpy-web-table-overflow-menu]
    [data-hpy-web-table-data-inner] {
    width: 1rem;
    padding: 0;
  }
`;
const LoadMoreBox = styled(Box)`
  position: sticky;
  left: 0;
  justify-content: center;
`;
const TableGrid = ({ children , ...props })=>{
    const [header] = useQueryChildComponents(children, [
        TableHeaderRow
    ]);
    if (header?.length !== 1) {
        throw new HappyWebSystemError("Table", "A single TableHeaderRow component is required to create a Table");
    }
    const [rows] = useQueryChildComponents(children, [
        TableRow
    ]);
    if (rows?.length === 0) {
        throw new HappyWebSystemError("Table", "At least one TableRow component is required to create a Table");
    }
    const [data] = useQueryChildComponents(rows[0].props.children, [
        TableData,
        TableOverflowMenu,
        TableDataSpacer
    ]);
    if (data?.length === 0) {
        throw new HappyWebSystemError("Table", "At least one TableData component is required to create a Table");
    }
    const columns = useMemo(()=>{
        const minMax = [];
        for (const c of React__default.Children.toArray(data)){
            const child = c;
            // Overflow menu spacing is handled explictly by always adding grid column at end.
            if (child.type.dataId === TableOverflowMenu.dataId) {
                continue;
            }
            minMax.push({
                min: child.props.min !== undefined ? child.props.min : 5,
                max: child.props.max !== undefined ? child.props.max : 8
            });
        }
        // Add grid column for Table overflow menu/spacer
        if (props.hideOverflow) {
            minMax.push({
                min: 0,
                max: 0
            });
        } else {
            minMax.push({
                min: 5,
                max: 5
            });
        }
        return {
            minMax,
            count: minMax.length
        };
    }, [
        data,
        props.hideOverflow
    ]);
    const [actionBar] = useQueryChildComponents(children, [
        TableActionBar
    ]);
    const [loadMoreButton] = useQueryChildComponents(children, [
        TableLoadMoreButton
    ]);
    const [loadMoreSpinner] = useQueryChildComponents(children, [
        LoadingSpinner
    ]);
    const tableRefs = useTableRefs();
    const colRefs = useMemo(()=>{
        // Setup the header refs for attachment to DOM elements elsewhere.
        tableRefs.headerRefs = [];
        for(let col = 0; col < columns.count; col++){
            tableRefs.headerRefs.push(createRef());
        }
        const leftBound = tableRefs.headerRefs[0];
        const left = tableRefs.headerRefs[1];
        const right = tableRefs.headerRefs[tableRefs.headerRefs.length - 2];
        const rightBound = tableRefs.headerRefs[tableRefs.headerRefs.length - 1];
        return {
            leftBound,
            left,
            right,
            rightBound
        };
    }, [
        columns.count,
        tableRefs
    ]);
    const leftBoundRect = useRect(colRefs.leftBound);
    const leftScroll = useIntersectionObserver(colRefs.left, {
        ratio: 1,
        containerMargin: `0px 0px 0px ${-(leftBoundRect?.width ?? 0) + (colRefs.left.current ? parseInt(window.getComputedStyle(colRefs.left.current).paddingLeft ?? "0") - SCROLL_HIGHLIGHT_WIDTH_REM * 16 : 0)}px`,
        containerRef: tableRefs.containerRef
    });
    const rightBoundRect = useRect(colRefs.rightBound);
    const rightScroll = useIntersectionObserver(colRefs.right, {
        ratio: 1,
        containerMargin: `0px ${-(rightBoundRect?.width ?? 0) + (colRefs.right.current ? parseInt(window.getComputedStyle(colRefs.right.current).paddingRight ?? "0") - SCROLL_HIGHLIGHT_WIDTH_REM * 16 : 0)}px 0px 0px`,
        containerRef: tableRefs.containerRef
    });
    const contRect = useRect(tableRefs.containerRef);
    return React__default.createElement(React__default.Fragment, null, React__default.createElement(StyledTable, {
        "data-hpy-web-table-grid": true,
        "data-hpy-web-table-grid-leftscroll": !leftScroll.intersecting,
        "data-hpy-web-table-grid-rightscroll": !rightScroll.intersecting,
        "data-hpy-web-table-grid-hide-overflow-column": props.hideOverflow,
        "$rows": rows.length,
        "$gridMinMax": columns.minMax
    }, header, React__default.createElement("tbody", null, rows), React__default.createElement(Portal, null, actionBar)), React__default.createElement(LoadMoreBox, {
        ref: tableRefs.scrollVerticalElementRef,
        padding: [
            1,
            0
        ],
        minHeight: 5,
        // Ensure the load spinner box has a width equal to the 'visible' width of the table (rect width - 5rem from the
        // final table column).
        style: {
            width: `${contRect?.width !== undefined ? contRect.width - 5 * 16 : 0}px`
        }
    }, loadMoreButton, loadMoreSpinner));
};
TableGrid.dataId = "data-hpy-web-table-grid";
TableGrid.displayName = "TableGrid";
const ScrollBox = styled(Box)`
  scroll-behavior: smooth;
`;
const StyledLoadingOverlay = styled(LoadingSpinnerOverlay)`
  ${setPortalLayer()}
`;
const TableGridBox = styled(Box)`
  flex-direction: column;
`;
const Table = ({ allIDs =[] , selectedIDs =[] , onToggle , onToggleAll , fallback =React__default.createElement(Fallback, null) , hideOverflow =false , loading =false , loadingMore =false , onLoadMore =undefined , hasNextPage =true , children , ...props })=>{
    if ((allIDs?.length > 0 || selectedIDs?.length > 0) && (onToggle === undefined || onToggleAll === undefined)) {
        throw new HappyWebSystemError("Table", "Handlers must be provided when using Table selection");
    }
    const [loadMoreButton] = useQueryChildComponents(children, [
        TableLoadMoreButton
    ]);
    if (loadMoreButton.length > 0 && onLoadMore !== undefined) {
        throw new HappyWebSystemError("Table", 'Only one of either the "Load More" button or infinite scroll handler must be used. Prefer infinite scroll as the "Load More" button as a pattern is deprecated and will eventually be removed.');
    }
    const [rows] = useQueryChildComponents(children, [
        TableRow
    ]);
    const toggle = useMemo(()=>onToggle ?? (()=>{
            return;
        }), [
        onToggle
    ]);
    const toggleAll = useMemo(()=>onToggleAll ?? (()=>{
            return;
        }), [
        onToggleAll
    ]);
    const containerRef = useRef();
    const scrollRef = useRef();
    const scroll = useInfiniteScroll({
        onLoadMore: onLoadMore ?? (()=>null),
        loading: loadingMore,
        hasNextPage,
        // Arbitrary margin so that items are loaded before 'bottom' of scroll is reached
        containerMargin: "0px 0px 250px 0px"
    });
    // Delay the loading exit, so that the Table has enough time to rerender before 'loading' is complete
    const transition = useTransition(loading, {
        from: {
            opacity: 0
        },
        enter: {
            opacity: 1
        },
        leave: {
            opacity: 0
        },
        trail: 200
    });
    return React__default.createElement(React__default.Fragment, null, transition((_, show)=>show && React__default.createElement(StyledLoadingOverlay, {
            testId: "Table",
            bgOverlay: "partial"
        })), !loading && rows.length === 0 && fallback, rows.length > 0 && React__default.createElement(TableProvider, {
        selected: selectedIDs,
        allIDs: allIDs,
        toggle: toggle,
        toggleAll: toggleAll
    }, React__default.createElement(TableRefsProvider, {
        containerRef: containerRef,
        scrollContainerRef: scrollRef,
        scrollVerticalElementRef: scroll.scrollElementRef
    }, React__default.createElement(Box, {
        "data-hpy-web-table": true,
        ...props,
        ref: containerRef,
        height: "fill",
        width: "fill",
        overflow: "hidden",
        padding: [
            0,
            2
        ]
    }, React__default.createElement(AutoSizer, null, ({ height , width  })=>React__default.createElement(ScrollBox, {
            ref: (ref)=>{
                scroll.containerRef(ref);
                scrollRef.current = ref;
            },
            overflow: "scroll",
            style: {
                height: `${height}px`,
                width: `${width}px`
            },
            padding: [
                0,
                0,
                2,
                0
            ]
        }, React__default.createElement(TableGridBox, {
            height: "fill",
            flexGrow: 1
        }, React__default.createElement(TableGrid, {
            hideOverflow: hideOverflow
        }, children, loadingMore && React__default.createElement(LoadingSpinner, {
            size: "medium",
            testId: "Table"
        })))))))));
};
Table.dataId = "data-hpy-web-table";
Table.displayName = "Table";
const TableSelectAll = ()=>{
    const table = useTable();
    if (table.toggleAll === undefined) {
        throw new HappyWebSystemError("TableSelectAll", "Handlers must be provided when using Table selection");
    }
    let value = table.selected.length === table.allIDs.length;
    if (table.selected.length !== 0) {
        value = undefined;
    }
    return React__default.createElement(CheckboxInput, {
        testId: "TableSelectAll-",
        showIndeterminate: true,
        checked: table.selected.length === table.allIDs.length,
        value: value,
        onChange: ()=>table.toggleAll()
    });
};
TableSelectAll.dataId = "data-hpy-web-table-select-all";
TableSelectAll.displayName = "TableSelectAll";
const TableSelectRow = ()=>{
    const table = useTable();
    if (table.toggle === undefined) {
        throw new HappyWebSystemError("TableSelectRow", "Handlers must be provided when using Table selection");
    }
    const row = useTableRow();
    return React__default.createElement(CheckboxInput, {
        testId: "TableSelectRow-" + row.id,
        checked: table.selected.includes(row.id),
        onChange: ()=>table.toggle(row.id)
    });
};
TableSelectRow.dataId = "data-hpy-web-table-select-row";
TableSelectRow.displayName = "TableSelectRow";
const ACTION_BAR_POSITION_REM = 3;
const ActionBar = animated(styled(Box)`
  height: ${(props)=>props.minHeight}rem;
  max-width: 45rem;

  position: absolute;
  bottom: ${ACTION_BAR_POSITION_REM}rem;
  left: calc(50% - 22.5rem);

  color: ${(props)=>props.theme.COLORS.context};
  background: ${(props)=>props.theme.COLORS.context10};
  border-radius: 6.25rem;

  ${setPortalLayer()}
`);
const TableActionBar = ({ descriptor , children , ...props })=>{
    const { selected  } = useTable();
    const transitions = useTransition(selected.length > 0, {
        from: {
            transform: `translateY(${2 * ACTION_BAR_POSITION_REM}rem)`,
            opacity: "0"
        },
        enter: {
            transform: "translateY(0rem)",
            opacity: "1"
        },
        leave: {
            transform: `translateY(${2 * ACTION_BAR_POSITION_REM}rem)`,
            opacity: "0"
        },
        config: config.gentle
    });
    const t = useTranslate();
    return transitions((styles, show)=>show && React__default.createElement(ActionBar, {
            style: styles,
            "data-hpy-web-list-action-bar": true,
            width: "fill",
            minHeight: 3,
            ...props
        }, React__default.createElement(Inline, {
            width: "fill"
        }, React__default.createElement(Box, {
            padding: [
                0,
                1.25
            ],
            flexGrow: 1
        }, React__default.createElement(Inline, {
            gap: 0.5,
            alignY: "center"
        }, React__default.createElement(Badge, {
            color: "white",
            bgColor: "context"
        }, selected.length), React__default.createElement(Text, null, t("system:Table.selected", {
            descriptor
        })))), React__default.createElement(Box, null, React__default.createElement(Inline, {
            gap: 0.5,
            alignY: "center"
        }, children)))));
};
TableActionBar.dataId = "data-hpy-web-table-action-bar";
TableActionBar.displayName = "TableActionBar";
const TableActionButton = ({ children , ...props })=>{
    return React__default.createElement(Button, {
        "data-hpy-web-table-action-button": true,
        ...props,
        variant: "context-inverse"
    }, children);
};
TableActionButton.dataId = "data-hpy-web-table-action-button";
TableActionButton.displayName = "TableActionButton";
/**
 * @deprecated Replaced by infinite scroll pattern - handlers can now be placed directly on the Table component.
 */ const TableLoadMoreButton = ({ children , ...props })=>{
    return React__default.createElement(Button, {
        "data-hpy-web-table-load-more-button": true,
        ...props,
        variant: "context-secondary"
    }, children);
};
TableLoadMoreButton.dataId = "data-hpy-web-table-load-more-button";
TableLoadMoreButton.displayName = "TableLoadMoreButton";
const useTableSelection = (all, onToggle)=>{
    const list = useListSelection(all, onToggle);
    const props = useMemo(()=>{
        return {
            allIDs: all,
            selectedIDs: list.selected,
            onToggle: list.toggle,
            onToggleAll: list.toggleAll
        };
    }, [
        all,
        list.selected,
        list.toggle,
        list.toggleAll
    ]);
    const value = useMemo(()=>{
        return {
            ...list,
            props
        };
    }, [
        list,
        props
    ]);
    return value;
};

export { Table, TableActionBar, TableActionButton, TableLoadMoreButton, TableSelectAll, TableSelectRow, useTableRefs, useTableSelection };
